import React from "react";
import styled from "styled-components";

import { LOCALES, Locale, useTranslations } from "lib/i18n";
import LanguageLink from "lib/components/LanguageLink";

/**
 * Footer language switcher: every language at once, in a flat row.
 *
 * The header has the compact dropdown; this one stays flat on purpose. It costs
 * no interaction to read, and on a translated page it gives a crawler a static
 * list of the alternates. With three to eight languages the row still fits, and
 * if it stops fitting it should wrap rather than become a second dropdown.
 *
 * Rendered unconditionally so a Spanish speaker can find the Spanish site from any
 * page, translated or not -- the whole point of the no-forced-redirect policy is
 * that finding the other language must be easy. On an untranslated page the
 * options are still here and still work, but LanguageLink renders them as buttons
 * rather than links, because there is no Spanish version of that page to point a
 * crawler at.
 */
const LanguageSwitcher: React.FC = () => {
  const t = useTranslations("common");

  return (
    <Nav aria-label={t("languageSwitcher.label")}>
      {LOCALES.map((option: Locale) => (
        <LanguageLink key={option} locale={option}>
          {(label, isActive) => <Option $isActive={isActive}>{label}</Option>}
        </LanguageLink>
      ))}
    </Nav>
  );
};

const Nav = styled.nav`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const Option = styled.span<{ $isActive: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 500)};
  color: inherit;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.7)};

  &:hover {
    opacity: 1;
  }
`;

export default LanguageSwitcher;
